export type MeasurementMarketItem = 'ribbon' | 'rod' | 'parcel';
export type MeasurementUnit = 'inches' | 'centimeters';

export interface MeasurementChallengeConfig {
  readonly kind: 'measure';
  readonly id: string;
  readonly skillId: string;
  readonly item: MeasurementMarketItem;
  readonly itemLabel: string;
  readonly unit: MeasurementUnit;
  readonly start: number;
  readonly end: number;
  readonly estimateChoices: readonly number[];
  readonly estimateAnswer: number;
  readonly hints: readonly string[];
}

export interface MarketDataChallengeConfig {
  readonly kind: 'data';
  readonly id: string;
  readonly skillId: string;
  readonly prompt: string;
  readonly choices: readonly number[];
  readonly answer: number;
  readonly unit: MeasurementUnit;
  readonly hints: readonly string[];
}

export type MeasurementMarketChallengeConfig = MeasurementChallengeConfig | MarketDataChallengeConfig;

export function getMeasurementAnswer(config: MeasurementMarketChallengeConfig) {
  return config.kind === 'measure' ? config.end - config.start : config.answer;
}

export function validateMeasurementMarketChallenge<T extends MeasurementMarketChallengeConfig>(config: T) {
  if (!config.id.trim() || !config.skillId.trim() || config.hints.length < 3) {
    throw new RangeError('Measurement Market requires identifiers and three progressive hints.');
  }
  if (config.kind === 'measure') {
    if (![config.start, config.end, config.estimateAnswer, ...config.estimateChoices].every(Number.isInteger)
      || config.start < 0 || config.end > 12 || config.end <= config.start) {
      throw new RangeError('Measurement Market ruler values must be whole numbers from 0 through 12.');
    }
    if (config.estimateChoices.length !== 3 || !config.estimateChoices.includes(config.estimateAnswer)) {
      throw new RangeError('Measurement Market measure challenges require three choices and one authored estimate.');
    }
  } else if (config.choices.length !== 3 || !config.choices.includes(config.answer)) {
    throw new RangeError('Measurement Market data challenges require three choices containing the answer.');
  }
  return config;
}

export const MEASUREMENT_MARKET_CHALLENGES = [
  validateMeasurementMarketChallenge({
    kind: 'measure',
    id: 'measurement-market-ribbon-inches',
    skillId: 'math-measure-length-inches',
    item: 'ribbon',
    itemLabel: 'royal ribbon',
    unit: 'inches',
    start: 0,
    end: 6,
    estimateChoices: [3, 5, 10],
    estimateAnswer: 5,
    hints: [
      'An estimate should be close, but it does not need to be exact.',
      'The ribbon looks longer than 3 inches and much shorter than 10 inches.',
      'Choose 5 as the close estimate, then use the ruler to find the exact length.',
    ],
  }),
  validateMeasurementMarketChallenge({
    kind: 'measure',
    id: 'measurement-market-crystal-centimeters',
    skillId: 'math-measure-length-centimeters',
    item: 'rod',
    itemLabel: 'crystal rod',
    unit: 'centimeters',
    start: 0,
    end: 9,
    estimateChoices: [4, 8, 12],
    estimateAnswer: 8,
    hints: [
      'Compare the rod to the whole 12-centimeter instrument before measuring.',
      'The rod is longer than 4 centimeters and not quite 12 centimeters.',
      'Choose 8 as the close estimate. Then read the tick where the rod ends.',
    ],
  }),
  validateMeasurementMarketChallenge({
    kind: 'measure',
    id: 'measurement-market-parcel-offset',
    skillId: 'math-measure-length-nonzero-start',
    item: 'parcel',
    itemLabel: 'market parcel',
    unit: 'inches',
    start: 2,
    end: 10,
    estimateChoices: [4, 7, 11],
    estimateAnswer: 7,
    hints: [
      'The parcel does not begin at zero, so the ending tick is not its length.',
      'Count the spaces from tick 2 to tick 10.',
      'Subtract the start from the end: 10 − 2 gives the parcel length.',
    ],
  }),
  validateMeasurementMarketChallenge({
    kind: 'data',
    id: 'measurement-market-order-records',
    skillId: 'math-interpret-measurement-data',
    prompt: 'The ribbon is 6 inches. The parcel is 8 inches. How many inches longer is the parcel?',
    choices: [1, 2, 4],
    answer: 2,
    unit: 'inches',
    hints: [
      'Compare the two recorded lengths: 6 inches and 8 inches.',
      'Find the gap between 6 and 8 on the measuring instrument.',
      'Count on from 6: 7, 8. The difference is 2 inches.',
    ],
  }),
] as const satisfies readonly MeasurementMarketChallengeConfig[];

export const MEASUREMENT_MARKET_COMPLETION_EVENT = 'measurement-market-route-restored';
